import { POSTER_PAGE, type PosterDoc, type PosterElement, type PosterImageElement, type PosterImageSource, type PosterShapeElement } from "@studio/shared";
import { FLOWER_CENTER, IMAGE_BORDER_MM, parseGradient, ROUND_RADIUS_MM, SHAPE_STROKE_MM, STICKER_PATHS } from "./art";
import { cssWeight, posterFont } from "./fonts";
import { canvasMeasure, layoutText, MM_PER_PT, type TextLayout } from "./geometry";
import { QR_QUIET, qrModules } from "./PosterElementView";
import { docForRender } from "./autoQr";

/**
 * 포스터를 «바이트로» 그리는 쪽 — PNG(canvas)·PDF·AR 마커가 같이 쓴다(D5·D7).
 * 🔑 줄바꿈은 편집 화면과 같은 `layoutText` 결과를 쓴다. 글꼴을 불러온 뒤에 불러야 한다.
 */

export type ImageKey = string;
export const imageKey = (source: PosterImageSource): ImageKey =>
  source.kind === "slot" ? `slot:${source.slot}` : `photo:${source.asset_id}`;

export interface RenderInput {
  doc: PosterDoc;
  qrUrl: string | null;
  images: Map<ImageKey, ImageBitmap>;
}

export function textLayouts(doc: PosterDoc): Map<string, TextLayout> {
  const map = new Map<string, TextLayout>();
  for (const element of doc.els) if (element.type === "text") map.set(element.id, layoutText(element, canvasMeasure));
  return map;
}

export function elementBoxHeight(element: PosterElement, layouts: Map<string, TextLayout>): number {
  return element.type === "text" ? layouts.get(element.id)?.height ?? 10 : element.h;
}

/* ───────────── 공통 모양 경로(요소 왼쪽 위가 원점, 단위는 호출자가 정한다) ───────────── */

export function roundedRectPath(w: number, h: number, r: number): string {
  const radius = Math.max(0, Math.min(r, w / 2, h / 2));
  if (radius === 0) return `M0 0H${w}V${h}H0Z`;
  return [
    `M${radius} 0`, `H${w - radius}`, `A${radius} ${radius} 0 0 1 ${w} ${radius}`,
    `V${h - radius}`, `A${radius} ${radius} 0 0 1 ${w - radius} ${h}`,
    `H${radius}`, `A${radius} ${radius} 0 0 1 0 ${h - radius}`,
    `V${radius}`, `A${radius} ${radius} 0 0 1 ${radius} 0Z`,
  ].join("");
}

export function shapePath(kind: PosterShapeElement["kind"] | PosterImageElement["shape"], w: number, h: number, unitsPerMm: number): string | null {
  if (kind === "circle") return null; // 타원은 따로 그린다
  if (kind === "triangle") return `M${w / 2} 0L${w} ${h}L0 ${h}Z`;
  if (kind === "pill") return roundedRectPath(w, h, Math.min(w, h) / 2);
  if (kind === "round") return roundedRectPath(w, h, ROUND_RADIUS_MM * unitsPerMm);
  return roundedRectPath(w, h, 0);
}

export function qrPath(url: string | null): { path: string; size: number } {
  const modules = qrModules(url ?? "https://studio.teachermate.co.kr");
  const size = modules.length + QR_QUIET * 2;
  const path = modules.flatMap((row, y) => row.flatMap((on, x) => on ? [`M${x + QR_QUIET} ${y + QR_QUIET}h1v1h-1z`] : [])).join("");
  return { path, size };
}

/* ───────────── 그림: 덮어 자르기(object-fit: cover) ───────────── */

export function coverCrop(sourceW: number, sourceH: number, boxW: number, boxH: number): { sx: number; sy: number; sw: number; sh: number } {
  const boxRatio = boxW / boxH;
  const sourceRatio = sourceW / sourceH;
  if (sourceRatio > boxRatio) {
    const sw = sourceH * boxRatio;
    return { sx: (sourceW - sw) / 2, sy: 0, sw, sh: sourceH };
  }
  const sh = sourceW / boxRatio;
  return { sx: 0, sy: (sourceH - sh) / 2, sw: sourceW, sh };
}

/**
 * 그림 요소 하나를 인쇄 해상도로 굽는다 — 덮어 자르기 + 모양(둥근·원) 오리기.
 * 네모는 JPEG(작다), 둥근·원은 투명이 필요해 PNG.
 */
export async function rasterizeImage(
  element: PosterImageElement,
  bitmap: ImageBitmap,
  dpi = 300,
  maxPx = 3000,
): Promise<{ bytes: Uint8Array; png: boolean }> {
  const crop = coverCrop(bitmap.width, bitmap.height, element.w, element.h);
  const wantW = element.w / 25.4 * dpi;
  const scale = Math.min(1, wantW / crop.sw, maxPx / crop.sw);
  const width = Math.max(1, Math.round(crop.sw * scale));
  const height = Math.max(1, Math.round(crop.sh * scale));
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext("2d")!;
  const png = element.shape !== "rect";
  if (png) {
    const pxPerMm = width / element.w;
    if (element.shape === "circle") {
      context.beginPath();
      context.ellipse(width / 2, height / 2, width / 2, height / 2, 0, 0, Math.PI * 2);
      context.clip();
    } else {
      context.clip(new Path2D(roundedRectPath(width, height, ROUND_RADIUS_MM * pxPerMm)));
    }
  }
  context.drawImage(bitmap, crop.sx, crop.sy, crop.sw, crop.sh, 0, 0, width, height);
  const blob = await new Promise<Blob>((resolve, reject) =>
    canvas.toBlob((value) => value ? resolve(value) : reject(new Error("toBlob failed")), png ? "image/png" : "image/jpeg", 0.92)
  );
  return { bytes: new Uint8Array(await blob.arrayBuffer()), png };
}

/* ───────────── canvas(PNG · 마커 · 교사 미리보기) ───────────── */

function fillBackground(context: CanvasRenderingContext2D, background: string, width: number, height: number): void {
  const gradient = parseGradient(background);
  if (!gradient) {
    context.fillStyle = background;
    context.fillRect(0, 0, width, height);
    return;
  }
  // CSS linear-gradient 각도: 0deg = 위쪽, 시계 방향. 그라데이션 선 길이는 CSS 규칙과 같게.
  const angle = gradient.angle * Math.PI / 180;
  const dx = Math.sin(angle);
  const dy = -Math.cos(angle);
  const half = (Math.abs(width * dx) + Math.abs(height * dy)) / 2;
  const fill = context.createLinearGradient(width / 2 - dx * half, height / 2 - dy * half, width / 2 + dx * half, height / 2 + dy * half);
  fill.addColorStop(0, gradient.from);
  fill.addColorStop(1, gradient.to);
  context.fillStyle = fill;
  context.fillRect(0, 0, width, height);
}

/** 문서를 canvas 에 그린다. pxPerMm = 캔버스 폭 / 297. */
export function drawPosterToCanvas(context: CanvasRenderingContext2D, input: RenderInput, pxPerMm: number): void {
  const doc = docForRender(input);
  const layouts = textLayouts(doc);
  fillBackground(context, doc.bg, POSTER_PAGE.width * pxPerMm, POSTER_PAGE.height * pxPerMm);
  for (const element of doc.els) {
    const h = elementBoxHeight(element, layouts) * pxPerMm;
    const w = element.w * pxPerMm;
    context.save();
    context.globalAlpha = element.opacity;
    context.translate(element.x * pxPerMm + w / 2, element.y * pxPerMm + h / 2);
    if (element.rot) context.rotate(element.rot * Math.PI / 180);
    context.translate(-w / 2, -h / 2);
    if (element.type === "text") {
      const layout = layouts.get(element.id)!;
      const font = posterFont(element.font);
      const size = element.size * MM_PER_PT * pxPerMm;
      context.font = `${cssWeight(font, element.weight)} ${size}px "${font.family}"`;
      context.fillStyle = element.color;
      context.textBaseline = "alphabetic";
      layout.lines.forEach((line, index) => {
        const metrics = context.measureText(line || " ");
        const ascent = metrics.fontBoundingBoxAscent;
        const descent = metrics.fontBoundingBoxDescent;
        const lineHeight = layout.lineHeight * pxPerMm;
        const baseline = index * lineHeight + (lineHeight - (ascent + descent)) / 2 + ascent;
        const lineWidth = metrics.width;
        const x = element.align === "center" ? (w - lineWidth) / 2 : element.align === "right" ? w - lineWidth : 0;
        context.fillText(line, x, baseline);
      });
    } else if (element.type === "image") {
      const bitmap = input.images.get(imageKey(element.src));
      if (element.border !== "none") {
        const ring = IMAGE_BORDER_MM * pxPerMm;
        context.fillStyle = element.border;
        if (element.shape === "circle") {
          context.beginPath();
          context.ellipse(w / 2, h / 2, w / 2 + ring, h / 2 + ring, 0, 0, Math.PI * 2);
          context.fill();
        } else {
          context.save();
          context.translate(-ring, -ring);
          context.fill(new Path2D(roundedRectPath(w + ring * 2, h + ring * 2, element.shape === "round" ? ROUND_RADIUS_MM * pxPerMm + ring : 0)));
          context.restore();
        }
      }
      if (bitmap) {
        context.save();
        if (element.shape === "circle") {
          context.beginPath();
          context.ellipse(w / 2, h / 2, w / 2, h / 2, 0, 0, Math.PI * 2);
          context.clip();
        } else if (element.shape === "round") {
          context.clip(new Path2D(roundedRectPath(w, h, ROUND_RADIUS_MM * pxPerMm)));
        }
        const crop = coverCrop(bitmap.width, bitmap.height, w, h);
        context.drawImage(bitmap, crop.sx, crop.sy, crop.sw, crop.sh, 0, 0, w, h);
        context.restore();
      }
    } else if (element.type === "shape") {
      const strokeWidth = element.stroke === "none" ? 0 : SHAPE_STROKE_MM * pxPerMm;
      const path = element.kind === "circle"
        ? (() => { const p = new Path2D(); p.ellipse(w / 2, h / 2, w / 2, h / 2, 0, 0, Math.PI * 2); return p; })()
        : new Path2D(shapePath(element.kind, w, h, pxPerMm)!);
      if (element.fill !== "transparent") {
        context.fillStyle = element.fill;
        context.fill(path);
      }
      if (strokeWidth) {
        context.strokeStyle = element.stroke;
        context.lineWidth = strokeWidth;
        context.stroke(path);
      }
    } else if (element.type === "sticker") {
      context.scale(w / 100, h / 100);
      context.fillStyle = element.color;
      context.fill(new Path2D(STICKER_PATHS[element.kind]));
      if (element.kind === "flower") {
        context.globalAlpha = element.opacity * 0.85;
        context.fillStyle = "#ffffff";
        context.beginPath();
        context.arc(FLOWER_CENTER.cx, FLOWER_CENTER.cy, FLOWER_CENTER.r, 0, Math.PI * 2);
        context.fill();
      }
    } else if (element.type === "qr") {
      const qr = qrPath(input.qrUrl);
      context.fillStyle = "#ffffff";
      context.fillRect(0, 0, w, h);
      context.scale(w / qr.size, h / qr.size);
      context.fillStyle = "#000000";
      context.fill(new Path2D(qr.path));
    }
    context.restore();
  }
}

export async function renderPosterPng(input: RenderInput, widthPx = 2480): Promise<Blob> {
  const pxPerMm = widthPx / POSTER_PAGE.width;
  const canvas = document.createElement("canvas");
  canvas.width = widthPx;
  canvas.height = Math.round(POSTER_PAGE.height * pxPerMm);
  drawPosterToCanvas(canvas.getContext("2d")!, input, pxPerMm);
  return new Promise<Blob>((resolve, reject) =>
    canvas.toBlob((blob) => blob ? resolve(blob) : reject(new Error("toBlob failed")), "image/png")
  );
}

/** 이미지 원천을 미리 받아 둔다(같은 그림을 여러 요소가 써도 한 번만). */
export async function preloadImages(
  doc: PosterDoc,
  load: (source: PosterImageSource) => Promise<Blob | null>,
): Promise<Map<ImageKey, ImageBitmap>> {
  const sources = new Map<ImageKey, PosterImageSource>();
  for (const element of doc.els) if (element.type === "image") sources.set(imageKey(element.src), element.src);
  const images = new Map<ImageKey, ImageBitmap>();
  await Promise.all([...sources].map(async ([key, source]) => {
    const blob = await load(source).catch(() => null);
    if (!blob) return;
    try {
      images.set(key, await createImageBitmap(blob));
    } catch {
      // 못 읽는 그림은 빈칸으로 둔다(포스터 전체를 실패시키지 않는다).
    }
  }));
  return images;
}
